import { Button, Checkbox, Grid2, IconButton, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";
import { IoMdRadioButtonOff, IoMdRadioButtonOn } from "react-icons/io";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { apis, callApi } from "../../../api";
import { defaultTimezone } from "../../../constants/timezone";
import { AccountService } from "../../../hooks/useAccount";
import useScreenSize from "../../../hooks/useScreenSize";
import { TokenService } from "../../../hooks/useToken";
import { rules } from "../../../utils";
import { Container } from "../../common/Container/Container";
import { CommonCheckbox, CommonInput } from "../../common/index";

import "./create_account.scss";

export type TCreateAccountFieldValues = {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	password: string;
	confirmPassword: string;
	timezoneName: string;
	acceptTermsAndConditions: boolean;
	role: "USER" | "TEACHER";
};

export function CreateAccount() {
	const navigate = useNavigate();
	const { width } = useScreenSize();
	const isMobile = width <= 768;
	const location = useLocation();
	const defaultRole = location.state?.role ?? "USER";
	const [searchParams] = useSearchParams();

	const defaultValues = {
		firstName: "",
		lastName: "",
		email: "",
		phoneNumber: "",
		password: "",
		confirmPassword: "",
		timezoneName: "",
		acceptTermsAndConditions: false,
		role: defaultRole,
	};

	const { handleSubmit, setValue, control } =
		useForm<TCreateAccountFieldValues>({
			defaultValues,
			mode: "all",
		});

	const onClickCreateNewAccount = async (data: TCreateAccountFieldValues) => {
		const { firstName, lastName, email, phoneNumber, password, role } = data;

		try {
			const timezoneName = defaultTimezone;
			const body = {
				firstName,
				lastName,
				email,
				phoneNumber,
				password,
				timezoneName,
				role,
			};

			const response = await callApi({
				method: "POST",
				url: apis.REGISTER,
				body,
			});
			const { token, user } = response;
			const redirect = searchParams.get("redirect");

			TokenService.saveToken(token);
			AccountService.saveAccount(user);
			if (redirect) {
				navigate(redirect.replace(/_/g, "#"), { replace: true });
				return;
			}
			navigate("/training/dashboard");
		} catch (error: any) {
			if (error?.response?.data?.errors?.length > 0) {
				alert(error?.response?.data?.errors[0]?.msg);
			}
		}
	};

	return (
		<Container className="create-account-container">
			<div className="create-account-content-container">
				<Grid2 container className="create-account-box">
					{!isMobile && (
						<Grid2
							size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
							className="left-column">
							<Typography className="sign-up-text">SIGN UP</Typography>
						</Grid2>
					)}

					<Grid2
						size={{ xs: 12, sm: 12, md: 9, lg: 9, xl: 9 }}
						className="right-column">
						<IconButton
							className="back-icon"
							onClick={() => {
								navigate("/sign-in");
							}}>
							<FaChevronLeft className="normal-icon" />
						</IconButton>
						<Typography className="header-profile-text">WELCOME!</Typography>
						<Typography className="normal-text create-account-description-text">
							Let's create your new account. Tell us a little bit about
							yourself:
						</Typography>
						<form
							className="sign-up-form item-separator-small"
							onSubmit={handleSubmit(onClickCreateNewAccount)}>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<CommonInput
										control={control}
										name="firstName"
										placeholder="First name*"
										rules={{
											required: {
												value: true,
												message: "First name is required.",
											},
											minLength: rules.firstName.min,
											maxLength: rules.firstName.max,
										}}
									/>
								</Grid2>

								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<CommonInput
										control={control}
										name="lastName"
										placeholder="Last name*"
										rules={{
											required: {
												value: true,
												message: "Last name is required.",
											},
											minLength: rules.lastName.min,
											maxLength: rules.lastName.max,
										}}
									/>
								</Grid2>

								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<CommonInput
										control={control}
										name="email"
										type="email"
										placeholder="Your email*"
										rules={{
											required: {
												value: true,
												message: "Email is required.",
											},
											pattern: rules.email.pattern,
										}}
									/>
								</Grid2>

								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<CommonInput
										control={control}
										name="phoneNumber"
										type="tel"
										placeholder="Your phone number*"
										rules={{
											required: {
												value: true,
												message: "Phone number is required.",
											},
											pattern: rules.phoneNumber.pattern,
										}}
									/>
								</Grid2>
							</Grid2>

							<Controller<TCreateAccountFieldValues>
								control={control}
								name="role"
								render={({ field: { value } }) => {
									return (
										<div
											style={{
												marginTop: 32,
												alignItems: "center",
												display: "flex",
												gap: 32,
											}}>
											<Typography className="normal-text bold">
												You are
											</Typography>
											<div style={{ display: "flex", alignItems: "center" }}>
												<Checkbox
													onClick={() => {
														setValue("role", "USER");
													}}
													checked={value === "USER"}
													checkedIcon={
														<IoMdRadioButtonOn className="normal-icon" />
													}
													icon={<IoMdRadioButtonOff className="normal-icon" />}
												/>
												<Typography className="normal-text">Student</Typography>
											</div>
											<div style={{ display: "flex", alignItems: "center" }}>
												<Checkbox
													onClick={() => {
														setValue("role", "TEACHER");
													}}
													checked={value === "TEACHER"}
													checkedIcon={
														<IoMdRadioButtonOn className="normal-icon" />
													}
													icon={<IoMdRadioButtonOff className="normal-icon" />}
												/>
												<Typography className="normal-text">
													Instructor
												</Typography>
											</div>
										</div>
									);
								}}
							/>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<CommonInput
										control={control}
										name="password"
										type="password"
										placeholder="Password*"
										rules={{
											required: {
												value: true,
												message: "Password is required.",
											},
											pattern: rules.password.pattern,
										}}
									/>
								</Grid2>

								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<CommonInput
										control={control}
										name="confirmPassword"
										type="password"
										placeholder="Confirm password*"
										rules={{
											required: {
												value: true,
												message: "Confirm password is required.",
											},
											pattern: rules.password.pattern,
											validate: (value, formValues) => {
												if (value === formValues.password) {
													// It must return undefined; or return; here
													return;
												}
												return "Password does not match.";
											},
										}}
									/>
								</Grid2>
							</Grid2>

							<Grid2 container spacing={2} style={{ marginTop: 8 }}>
								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<CommonCheckbox
										control={control}
										name="acceptTermsAndConditions"
										rules={{
											required: {
												value: true,
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore
												message: (
													<p>
														You have to accept <b>Terms & Conditions</b>
													</p>
												),
											},
										}}
										label={
											<label
												className="small-text"
												style={{ fontSize: "12px", color: "#989898" }}>
												I accept the{" "}
												<b
													onClick={() => {
														window.open("/terms-and-conditions", "_blank");
													}}
													style={{
														fontWeight: "bold",
														textDecoration: "none",
														cursor: "pointer",
													}}>
													Terms & Conditions<sup>*</sup>
												</b>
											</label>
										}
									/>
								</Grid2>

								<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
									<div style={{ display: "flex", justifyContent: "flex-end" }}>
										<Button
											className="normal-button create-account-button"
											variant="contained"
											type="submit">
											Continue
										</Button>
									</div>
								</Grid2>
							</Grid2>
						</form>
						<Typography className="normal-text note">
							All sections with * must be filled
						</Typography>
					</Grid2>
				</Grid2>
			</div>
		</Container>
	);
}
